import { useEffect, useState } from 'react'
import { useT } from '@/i18n'
import { useToast } from '@/providers/UiProviders'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { ApiError } from '@/lib/api'
import { createAuction } from './gromAuktionApi'

export function CreateAuctionDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: () => void
}) {
  const t = useT()
  const { toast } = useToast()

  const [username, setUsername] = useState('')
  const [sellerUserId, setSellerUserId] = useState('0')
  const [startingPrice, setStartingPrice] = useState('')
  const [minIncrement, setMinIncrement] = useState('')
  const [hours, setHours] = useState('24')
  const [isNumber, setIsNumber] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setUsername('')
    setSellerUserId('0')
    setStartingPrice('')
    setMinIncrement('')
    setHours('24')
    setIsNumber(false)
    setError(null)
  }, [open])

  async function handleSubmit() {
    setError(null)
    const startingPriceNum = Number(startingPrice)
    const minIncrementNum = Number(minIncrement)
    const sellerNum = Number(sellerUserId)
    const hoursNum = Number(hours)
    if (!username.trim() || Number.isNaN(startingPriceNum) || startingPriceNum < 0 || !minIncrementNum || minIncrementNum <= 0 || !hoursNum || hoursNum <= 0 || Number.isNaN(sellerNum)) {
      setError(t.gromauktion.fillRequired)
      return
    }

    setSubmitting(true)
    try {
      await createAuction({
        username: username.trim(),
        seller_user_id: sellerNum,
        starting_price: startingPriceNum,
        min_bid_increment: minIncrementNum,
        duration_seconds: Math.round(hoursNum * 3600),
        is_number: isNumber,
      })
      toast(t.gromauktion.createSuccess, { tone: 'success' })
      onCreated()
      onClose()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t.gromauktion.createAuctionTitle}
      description={t.gromauktion.createListingDesc}
      width="sm"
      footer={
        <>
          <div className="flex-1" />
          <Button variant="default" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {t.gromauktion.createAuction}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Checkbox id="isNumberAuction" checked={isNumber} onChange={(e) => setIsNumber(e.target.checked)} />
          <label htmlFor="isNumberAuction" className="cursor-pointer text-sm text-secondary">
            {t.gromauktion.isNumber}
          </label>
        </div>
        <Field label={isNumber ? t.gromauktion.numberValue : t.gromauktion.username} required>
          <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder={isNumber ? '+888 0123 4567' : 'myname'} />
        </Field>
        <Field label={t.gromauktion.sellerUserId} hint={t.gromauktion.sellerUserIdHint}>
          <Input type="number" value={sellerUserId} onChange={(e) => setSellerUserId(e.target.value)} />
        </Field>
        <Field label={t.gromauktion.startingPrice} required>
          <Input type="number" min={0} value={startingPrice} onChange={(e) => setStartingPrice(e.target.value)} />
        </Field>
        <Field label={t.gromauktion.minBidIncrement} required>
          <Input type="number" min={1} value={minIncrement} onChange={(e) => setMinIncrement(e.target.value)} />
        </Field>
        <Field label={t.gromauktion.durationHours} required>
          <Input type="number" min={1} value={hours} onChange={(e) => setHours(e.target.value)} />
        </Field>
        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </Dialog>
  )
}
